import { useRequirePermissions } from '#~/routes/electron/permissions/permissions.js';
import * as Dialog from '@studio/design-system/dialog';
import { useComposedRefs } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { forwardRef, useRef } from 'react';
import { Context } from './context.js';
import { Scaffold } from './scaffold.jsx';
import type { Props as ProviderProps } from './store/index.js';
import { Mediator, Provider } from './store/index.js';
import type { Props } from './types.js';
import { useWorkflow } from './wiring.js';

const Workflow = forwardRef<Dialog.Api, Omit<Props, keyof ProviderProps | 'roomId'>>(
  (props, forwardedRef): ReactElement => {
    const apiRef = useRef<Dialog.Api>(null);
    const wiring = useWorkflow(apiRef);

    useRequirePermissions({
      title: 'Discover USB Devices',
      description: 'We need your permission to enable all features',
      required: ['camera', 'microphone']
    });

    return (
      <Dialog.Root
        onBeforeClose={wiring.handleBeforeClose}
        ref={useComposedRefs(apiRef, forwardedRef)}
        {...props}
      >
        <Dialog.Overlay>
          <Dialog.Panel>
            <Context.Provider value={wiring}>
              <Scaffold />
            </Context.Provider>
          </Dialog.Panel>
        </Dialog.Overlay>
      </Dialog.Root>
    );
  }
);

/** `<Add>` video source component. */
export const Add = forwardRef<Dialog.Api, Props>(
  (
    { agentIds, appId, devices, discoveries, onboarding, roomId, ...props },
    forwardedRef
  ): ReactElement => (
    <Provider
      agentIds={agentIds}
      appId={appId}
      devices={devices}
      discoveries={discoveries}
      key={'add-device-provider'}
      onboarding={onboarding}
      roomId={roomId}
    >
      <Mediator key='add-device-mediator'>
        <Workflow ref={forwardedRef} {...props} />
      </Mediator>
    </Provider>
  )
);
